/* global __APP_CONFIG__ */
import { Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useMsal } from '@azure/msal-react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useBranding } from '../context/BrandingContext'
import { usePermissions } from '../context/PermissionContext'
import { useTheme } from '../context/ThemeContext'
import useSystemHealth from '../hooks/useSystemHealth'
import useAppNavigation, { isNavigationItemActive } from '../navigation/useAppNavigation'
import { INTEGRATION_DESTINATIONS, INTEGRATIONS_SECTION } from '../../../../shared/appNavigation'
import DisplayControls from '../components/DisplayControls'
import { BRANDING_LOGO_CHANGED_EVENT, PUBLIC_BRANDING_LOGO_URL } from '../publicAssets'
import ProductIcon from '../icons/ProductIcon'
import { OpsButton, OpsSheet, StateLabel, ViewState } from '../ui/ops'
import Menu, { MENU_SEARCH_THRESHOLD } from '../ui/Menu'

export function firstDestination(section) {
  return section.homePath || section.items.find((navigationItem) => navigationItem.path)?.path || '/'
}

export function findNavigationContext(sections, pathname) {
  for (const section of sections) {
    for (const navigationItem of section.items) {
      if (navigationItem.children) {
        const child = navigationItem.children.find((candidate) => isNavigationItemActive(candidate, pathname))
        if (child) return { section, item: navigationItem, child }
      }
      if (isNavigationItemActive(navigationItem, pathname)) return { section, item: navigationItem, child: null }
    }
    if (section.homePath === pathname) {
      return {
        section,
        item: { id: `${section.id}-overview`, label: 'Overview', path: section.homePath },
        child: null,
      }
    }
  }
  if (pathname === '/') return { section: null, item: { label: 'Operational overview', path: '/' }, child: null }
  // An integration page the menu does not list yet (enabled, not set up) is still an integration.
  const integration = INTEGRATION_DESTINATIONS.find((destination) => isNavigationItemActive(destination, pathname))
  if (integration) {
    return {
      section: sections.find((section) => section.id === INTEGRATIONS_SECTION.id) || { ...INTEGRATIONS_SECTION, items: [] },
      item: integration,
      child: null,
    }
  }
  return {
    section: null,
    item: {
      label: pathname.split('/').filter(Boolean).at(-1)?.replaceAll('-', ' ') || 'Workspace',
      path: pathname,
    },
    child: null,
  }
}

export function Wordmark({ name, subtitle }) {
  const productName = name || 'GAEZLA'
  const productSubtitle = subtitle || 'Operations'
  const [logoVersion, setLogoVersion] = useState(null)

  useEffect(() => {
    const refreshLogo = () => setLogoVersion((currentVersion) => (
      Math.max(Date.now(), (currentVersion || 0) + 1)
    ))
    window.addEventListener(BRANDING_LOGO_CHANGED_EVENT, refreshLogo)
    return () => window.removeEventListener(BRANDING_LOGO_CHANGED_EVENT, refreshLogo)
  }, [])

  const logoSrc = logoVersion
    ? `${PUBLIC_BRANDING_LOGO_URL}?v=${logoVersion}`
    : PUBLIC_BRANDING_LOGO_URL

  return (
    <Link className="ops-wordmark" to="/" aria-label={`${productName} ${productSubtitle} home`}>
      <img className="ops-wordmark__logo" src={logoSrc} alt="" />
      <span className="ops-wordmark__copy"><strong>{productName}</strong><small>{productSubtitle}</small></span>
    </Link>
  )
}

function DismissibleMenu({ className, children }) {
  const menuRef = useRef(null)

  useEffect(() => {
    const handlePointerDown = (event) => {
      const menu = menuRef.current
      if (menu?.open && !menu.contains(event.target)) menu.open = false
    }

    document.addEventListener('pointerdown', handlePointerDown)
    return () => document.removeEventListener('pointerdown', handlePointerDown)
  }, [])

  return <details className={className} ref={menuRef}>{children}</details>
}

function HealthMenu() {
  const { health, loading, error } = useSystemHealth()
  const state = error ? 'unavailable' : health.state === 'degraded' ? 'critical' : 'observed'
  const label = error
    ? 'Health unavailable'
    : loading && health.counts.total === 0
      ? 'Reading health'
      : `${health.counts.healthy}/${health.counts.total} observed healthy`

  return (
    <DismissibleMenu className="ops-menu ops-health-menu">
      <summary aria-label={`System health: ${label}`}><StateLabel state={state} label={label} /></summary>
      <div className="ops-menu__panel ops-health-menu__panel">
        <header>
          <p className="ops-kicker">Observed systems</p>
          <h2>System health</h2>
          <p>{error ? 'The health endpoint could not be reached.' : 'Current reported state for configured systems.'}</p>
        </header>
        {health.groups.length === 0 && !error && <p className="ops-menu__empty">No systems are configured.</p>}
        {health.groups.map((group) => (
          <section key={group.key}>
            <h3>{group.label}</h3>
            {group.items.map((healthItem, index) => (
              healthItem.href ? (
                /^https?:\/\//i.test(healthItem.href) ? (
                  <a key={`${group.key}-${index}`} href={healthItem.href} target="_blank" rel="noreferrer">
                    <span>{healthItem.name}<small>{healthItem.reason || healthItem.detail}</small></span>
                    <StateLabel state={healthItem.state === 'degraded' ? 'critical' : 'observed'} />
                  </a>
                ) : (
                  <Link key={`${group.key}-${index}`} to={healthItem.href}>
                    <span>{healthItem.name}<small>{healthItem.reason || healthItem.detail}</small></span>
                    <StateLabel state={healthItem.state === 'degraded' ? 'critical' : 'observed'} />
                  </Link>
                )
              ) : (
                <div key={`${group.key}-${index}`} className="ops-menu__row">
                  <span>{healthItem.name}<small>{healthItem.reason || healthItem.detail}</small></span>
                  <StateLabel state={healthItem.state === 'degraded' ? 'critical' : 'observed'} />
                </div>
              )
            ))}
          </section>
        ))}
        <footer className="ops-provenance">
          {health.generated_at ? `Observed ${new Date(health.generated_at).toLocaleString()}` : 'Observation time unavailable'}
        </footer>
      </div>
    </DismissibleMenu>
  )
}

function AccountMenu() {
  const { instance } = useMsal()
  const navigate = useNavigate()
  const { appRoles, userEmail, isGlobalAdmin, refreshing, refreshPermissions } = usePermissions()
  const initials = useMemo(() => {
    const local = userEmail?.split('@')[0] || ''
    const parts = local.split('.')
    return parts.length > 1 ? `${parts[0][0]}${parts[1][0]}`.toUpperCase() : local.slice(0, 2).toUpperCase() || '?'
  }, [userEmail])

  return (
    <DismissibleMenu className="ops-menu ops-account-menu">
      <summary aria-label="Account menu"><span className={`ops-avatar${isGlobalAdmin() ? ' ops-avatar--admin' : ''}`}>{initials}</span></summary>
      <div className="ops-menu__panel ops-account-menu__panel">
        <header>
          <p className="ops-kicker">Current operator</p>
          <h2>{instance.getActiveAccount?.()?.name || userEmail || 'Account unavailable'}</h2>
          {userEmail && <p>{userEmail}</p>}
        </header>
        <div className="ops-account-menu__roles">
          {appRoles.length ? appRoles.map((role) => <span key={role}>{role}</span>) : <span>No roles reported</span>}
        </div>
        <nav aria-label="Account actions">
          <button type="button" onClick={refreshPermissions} disabled={refreshing}>{refreshing ? 'Refreshing permissions' : 'Refresh permissions'}</button>
          <button type="button" onClick={() => navigate('/me/settings')}>My settings</button>
          <button type="button" onClick={() => instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin })}>Log out</button>
        </nav>
      </div>
    </DismissibleMenu>
  )
}

function DisclosureGlyph() {
  return (
    <svg className="ops-disclosure" viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" focusable="false">
      <path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function RouteTrail({ context }) {
  return (
    <>
      <span>{context.section?.label || 'Operations'}</span>
      <b aria-hidden="true">/</b>
      {context.child && (
        <>
          <span>{context.item.label}</span>
          <b aria-hidden="true">/</b>
        </>
      )}
      <strong>{context.child?.label || context.item.label}</strong>
    </>
  )
}

export function locationLabel(context) {
  return [
    context.section?.label || 'Operations',
    context.child ? context.item.label : null,
    context.child?.label || context.item.label,
  ].filter(Boolean).join(' / ')
}

/* One navigation sheet for every tier. It opens from the location control and lists every
   permitted destination in the same fixed order as the domain nav. The order never changes;
   only the "You are here" mark and the current-page mark move. */
function NavigationSheet({ sections, currentSectionId, pathname, loading, open, onClose, triggerRef, onAsk }) {
  return (
    <OpsSheet open={open} title="Menu" onClose={onClose} returnFocusRef={triggerRef} anchor="start" className="ops-menu-launcher">
      {onAsk && (
        <button type="button" className="ops-menu-ask" aria-haspopup="dialog" onClick={onAsk}>
          <ProductIcon name="ui/search" size={16} />
          <span>Ask where something is…</span>
        </button>
      )}
      {loading ? (
        <ViewState kind="loading" title="Reading available work" detail="Checking current permissions, entitlements, and configured integrations." />
      ) : sections.length === 0 ? (
        <ViewState kind="empty" title="No destinations available" detail="No operational destination is currently available to this operator." />
      ) : <nav aria-label="All destinations">
        {sections.map((section) => {
          const current = section.id === currentSectionId
          return (
            <section key={section.id} className={current ? 'is-current' : undefined}>
              <header>
                <p className="ops-kicker">
                  {section.label}
                  {current && <small className="ops-launcher-here">You are here</small>}
                </p>
                {section.homePath && (
                  <Link to={section.homePath} onClick={onClose} aria-current={pathname === section.homePath ? 'page' : undefined}>Open overview</Link>
                )}
              </header>
              <div>
                {section.items.map((navigationItem) => {
                  const children = navigationItem.children || []
                  const itemCurrent = children.length === 0 && isNavigationItemActive(navigationItem, pathname)
                  return (
                    <div key={navigationItem.id} className="ops-launcher-item">
                      {navigationItem.path ? (
                        <Link to={navigationItem.path} onClick={onClose} aria-current={itemCurrent ? 'page' : undefined}>
                          <span>{navigationItem.label}</span>
                          {navigationItem.count != null && <small>{navigationItem.count}</small>}
                        </Link>
                      ) : <span>{navigationItem.label}</span>}
                      {children.length > 0 && (
                        <div className="ops-launcher-item__children">
                          {children.map((child) => {
                            const childCurrent = isNavigationItemActive(child, pathname)
                            return (
                              <Link key={child.id} to={child.path} onClick={onClose} aria-current={childCurrent ? 'page' : undefined}>
                                {child.label}
                              </Link>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </section>
          )
        })}
      </nav>}
    </OpsSheet>
  )
}

// A section with more than 15 destinations gets a searchable overflow menu that
// ends with the integration catalogue (NN/g: a searchable list above about 15 items).
export const SECTION_SEARCH_THRESHOLD = MENU_SEARCH_THRESHOLD
const CATALOG_LINK = { id: 'available-integrations', label: 'Available integrations', href: '/settings?tab=integrations-catalog' }

/* Entries that fit stay in the strip; the rest go into a More menu at its end, measured
   by the browser (ResizeObserver) the same way ActionBar does. The current entry is never
   moved into the menu. With no layout (jsdom) every entry renders inline. Both the domain
   nav and the task strip use it. */
function useStripOverflow(items, activeIndex) {
  const navRef = useRef(null)
  const resetKey = items.map((navigationItem) => navigationItem.id).join('|')
  const [shown, setShown] = useState(items.length)
  const [measuredFor, setMeasuredFor] = useState(resetKey)
  // Bumped on every resize so the strip is measured again even when the count is unchanged.
  const [layoutTick, setLayoutTick] = useState(0)
  const count = measuredFor === resetKey ? Math.min(shown, items.length) : items.length

  useLayoutEffect(() => {
    setMeasuredFor(resetKey)
    setShown(items.length)
    const nav = navRef.current
    if (!nav || typeof globalThis.ResizeObserver === 'undefined') return undefined
    const observer = new globalThis.ResizeObserver(() => {
      setShown(items.length)
      setLayoutTick((tick) => tick + 1)
    })
    observer.observe(nav)
    if (nav.parentElement) observer.observe(nav.parentElement)
    return () => observer.disconnect()
  }, [resetKey, items.length])

  useLayoutEffect(() => {
    const nav = navRef.current
    if (!nav || typeof globalThis.ResizeObserver === 'undefined') return
    if (count > 1 && nav.scrollWidth > nav.clientWidth) setShown(count - 1)
  }, [count, resetKey, activeIndex, layoutTick])

  const inline = new Set(items.slice(0, count).map((navigationItem) => navigationItem.id))
  if (count < items.length && activeIndex >= count) {
    inline.delete(items[count - 1]?.id)
    inline.add(items[activeIndex].id)
  }
  return { navRef, inline }
}

function overflowMenuItems(items) {
  const out = []
  for (const navigationItem of items) {
    const children = navigationItem.children || []
    if (children.length > 0) {
      out.push({ type: 'group', label: navigationItem.label })
      if (navigationItem.path) out.push({ id: navigationItem.id, label: navigationItem.label, href: navigationItem.path })
      for (const child of children) out.push({ id: child.id, label: child.label, href: child.path })
    } else if (navigationItem.path) {
      out.push({ id: navigationItem.id, label: navigationItem.label, href: navigationItem.path })
    }
  }
  return out
}

function destinationCount(items) {
  return items.reduce((total, navigationItem) => total + Math.max(1, (navigationItem.children || []).length), 0)
}

function DesktopSectionNavigation({ items, pathname, sectionLabel, sectionId }) {
  const activeIndex = items.findIndex((navigationItem) => (
    isNavigationItemActive(navigationItem, pathname)
    || (navigationItem.children || []).some((child) => isNavigationItemActive(child, pathname))
  ))
  const { navRef, inline } = useStripOverflow(items, activeIndex)
  const overflow = items.filter((navigationItem) => !inline.has(navigationItem.id))
  const longSection = destinationCount(items) > SECTION_SEARCH_THRESHOLD
  const menuItems = overflowMenuItems(overflow)
  if (menuItems.length > 0 && longSection && sectionId === 'integrations') menuItems.push({ type: 'separator' }, CATALOG_LINK)

  return (
    <nav ref={navRef} className="ops-task-nav" aria-label={`${sectionLabel} destinations`}>
      {items.filter((navigationItem) => inline.has(navigationItem.id)).map((navigationItem) => {
        const children = navigationItem.children || []
        const activeChild = children.find((child) => isNavigationItemActive(child, pathname))
        const groupActive = Boolean(activeChild) || isNavigationItemActive(navigationItem, pathname)
        const parentClassName = children.length > 0
          ? (groupActive ? 'is-group-current' : undefined)
          : (groupActive ? 'is-current' : undefined)

        return (
          <div key={navigationItem.id} className={`ops-task-group${groupActive ? ' is-expanded' : ''}`}>
            {navigationItem.path ? (
              <Link
                to={navigationItem.path}
                className={parentClassName}
                aria-current={children.length === 0 && groupActive ? 'page' : undefined}
              >
                {navigationItem.label}
              </Link>
            ) : <span className={parentClassName}>{navigationItem.label}</span>}
            {groupActive && children.length > 0 && (
              <div className="ops-task-nav__children">
                {children.map((child) => {
                  const current = isNavigationItemActive(child, pathname)
                  return (
                    <Link
                      key={child.id}
                      to={child.path}
                      className={current ? 'is-current' : undefined}
                      aria-current={current ? 'page' : undefined}
                    >
                      {child.label}
                    </Link>
                  )
                })}
              </div>
            )}
          </div>
        )
      })}
      {menuItems.length > 0 && (
        <Menu
          label={`More ${sectionLabel} destinations`}
          items={menuItems}
          search={longSection || undefined}
          trigger={(props) => (
            <button {...props} type="button" className="ops-task-nav__more">
              More
              <DisclosureGlyph />
            </button>
          )}
        />
      )}
    </nav>
  )
}

/* The domains that fit stay in the brand row; the rest go into a More menu, so the row never
   runs under the "Where is …?" field or the utilities (an interface scale zooms the root,
   which the width tiers' media queries do not see). */
function DomainNavigation({ sections, currentSectionId }) {
  const activeIndex = sections.findIndex((section) => section.id === currentSectionId)
  const { navRef, inline } = useStripOverflow(sections, activeIndex)
  const menuItems = sections
    .filter((section) => !inline.has(section.id))
    .map((section) => ({ id: section.id, label: section.label, href: firstDestination(section) }))

  return (
    <nav ref={navRef} className="ops-domain-nav" aria-label="Operational domains">
      {sections.filter((section) => inline.has(section.id)).map((section) => (
        <Link
          key={section.id}
          to={firstDestination(section)}
          className={section.id === currentSectionId ? 'is-current' : undefined}
        >
          {section.label}
        </Link>
      ))}
      {menuItems.length > 0 && (
        <Menu
          label="More operational domains"
          items={menuItems}
          trigger={(props) => (
            <button {...props} type="button" className="ops-domain-nav__more">
              More
              <DisclosureGlyph />
            </button>
          )}
        />
      )}
    </nav>
  )
}

/* `assistant` is the navigation assistant the application shell composes in (platform code
   never imports a feature). It renders the desktop "Where is …?" field in its header slot
   and owns the ask sheet, which the header opens from its phone search icon and from the
   field at the top of the menu. */
export default function GaezlaHeader({ assistant: Assistant = null }) {
  const { pathname } = useLocation()
  const { loading: navigationLoading, sections } = useAppNavigation()
  const { theme, toggleTheme } = useTheme()
  const branding = useBranding()
  const [navigationOpen, setNavigationOpen] = useState(false)
  const [askOpen, setAskOpen] = useState(false)
  const navigationButtonRef = useRef(null)
  const askButtonRef = useRef(null)
  const askReturnFocusRef = useRef(null)
  const closeNavigation = useCallback(() => setNavigationOpen(false), [])
  const setAskSheetOpen = useCallback((open, returnFocusTo = document.activeElement) => {
    if (open) askReturnFocusRef.current = typeof returnFocusTo?.focus === 'function' ? returnFocusTo : null
    setAskOpen(open)
  }, [])
  const askFromMenu = useCallback(() => {
    setNavigationOpen(false)
    setAskSheetOpen(true, navigationButtonRef.current)
  }, [setAskSheetOpen])
  const context = findNavigationContext(sections, pathname)
  const activeSectionItems = context.section?.items || []
  const config = window.__APP_CONFIG__ || __APP_CONFIG__

  return (
    <>
      {config?.debugMode && <div className="ops-debug-banner">Debug environment: {config.environment}</div>}
      <header className="ops-header">
        <div className="ops-header__primary">
          <Wordmark name={branding.name} subtitle={branding.subtitle} />
          <DomainNavigation sections={sections} currentSectionId={context.section?.id} />
          {Assistant && (
            <div className="ops-ask-slot">
              <Suspense fallback={null}>
                <Assistant sheetOpen={askOpen} onSheetOpenChange={setAskSheetOpen} sheetReturnFocusRef={askReturnFocusRef} />
              </Suspense>
            </div>
          )}
          <div className="ops-header__tools">
            <HealthMenu />
            <OpsButton tone="quiet" className="ops-header__utility" onClick={toggleTheme} aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}>
              {theme === 'dark' ? 'Light' : 'Dark'}
            </OpsButton>
            <AccountMenu />
          </div>
        </div>
        <div className="ops-header__context">
          {/* The location is the menu: where you are, and the way to everywhere else. */}
          <button
            ref={navigationButtonRef}
            className="ops-location-trigger"
            type="button"
            aria-haspopup="dialog"
            aria-expanded={navigationOpen}
            aria-label={`Menu: ${locationLabel(context)}`}
            onClick={() => setNavigationOpen(true)}
          >
            <span className="ops-route-trail"><RouteTrail context={context} /></span>
            <DisclosureGlyph />
          </button>
          {Assistant && (
            <button
              ref={askButtonRef}
              className="ops-ask-trigger"
              type="button"
              aria-label="Ask where something is"
              aria-haspopup="dialog"
              aria-expanded={askOpen}
              onClick={() => setAskSheetOpen(true, askButtonRef.current)}
            >
              <ProductIcon name="ui/search" size={18} />
            </button>
          )}
          {activeSectionItems.length > 0 && (
            <DesktopSectionNavigation items={activeSectionItems} pathname={pathname} sectionLabel={context.section.label} sectionId={context.section.id} />
          )}
          <div className="ops-context-actions">
            <DisplayControls />
          </div>
        </div>
      </header>
      <NavigationSheet
        sections={sections}
        currentSectionId={context.section?.id}
        pathname={pathname}
        loading={navigationLoading}
        open={navigationOpen}
        onClose={closeNavigation}
        triggerRef={navigationButtonRef}
        onAsk={Assistant ? askFromMenu : undefined}
      />
    </>
  )
}
